import { PageIntro } from "@/components/kit/PageIntro";
import { usePageMeta } from "@/components/site/Seo";
import { SITE } from "@/lib/content";

const UPDATED = "24 October 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="font-display text-2xl text-forest sm:text-3xl">{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-relaxed text-charcoal-soft">{children}</div>
    </section>
  );
}

export default function Privacy() {
  usePageMeta("Privacy Policy", `How ${SITE.name} collects, uses and protects the information you share with us.`);

  return (
    <div data-testid="privacy-page">
      <PageIntro
        testId="privacy-hero"
        eyebrow="Privacy"
        lines={["Your information,", <em key="h" className="text-gold-soft">handled with care.</em>]}
        intro="What we collect, why we collect it, and what we will never do with it. Written plainly, because you should not need a lawyer to understand a privacy policy."
      />

      <div className="bg-beige py-20 text-charcoal">
        <div className="wrap max-w-3xl">
          <p className="text-sm text-charcoal-soft">Last updated: {UPDATED}</p>

          <Section title="Who we are">
            <p>
              {SITE.name} is a wellness coaching practice run by {SITE.founder} at {SITE.street}, {SITE.locality},{" "}
              {SITE.cityName}, {SITE.region} {SITE.postalCode}, India. You can reach us at{" "}
              <a className="link-line text-forest" href={`mailto:${SITE.email}`}>{SITE.email}</a> or on{" "}
              <a className="link-line text-forest" href={`tel:${SITE.phoneTel}`}>{SITE.phoneDisplay}</a>.
            </p>
            <p>This policy covers this website and the WhatsApp number we use to talk to clients and enquirers.</p>
          </Section>

          <Section title="What we collect">
            <p>Only what we need to answer you and to coach you properly.</p>
            <ul className="ml-5 list-disc space-y-2">
              <li><strong>When you enquire:</strong> your name, phone number, and whatever you choose to tell us about your goal.</li>
              <li><strong>When you message us on WhatsApp:</strong> your phone number, your WhatsApp profile name, and the messages you send.</li>
              <li><strong>When you join the programme:</strong> measurements such as height, weight, body composition and body age; your eating and activity routine; and any health information or reports you choose to share so we can build a sensible plan.</li>
              <li><strong>When you browse this site:</strong> standard technical data such as pages visited, collected to understand what is useful.</li>
            </ul>
            <p>We do not ask for payment card details on this website, and we do not store them.</p>
          </Section>

          <Section title="Why we use it">
            <ul className="ml-5 list-disc space-y-2">
              <li>To reply to your enquiry and arrange your free consultation.</li>
              <li>To build and adjust your plan, and to follow up with you during the programme.</li>
              <li>To send you reminders and programme-related messages on WhatsApp.</li>
              <li>To improve this website and our service.</li>
            </ul>
            <p>
              We send marketing or promotional messages only to people who have agreed to receive them. You can tell us to stop at any
              time, by replying STOP on WhatsApp or emailing us, and we will.
            </p>
          </Section>

          <Section title="Health information">
            <p>
              Anything you share about your health is treated as confidential. It is used only to guide your nutrition and lifestyle
              coaching, is seen only by {SITE.founder} and anyone directly supporting your programme, and is never sold, published or
              shared with advertisers.
            </p>
            <p>
              {SITE.coachLine} We provide nutrition and lifestyle coaching alongside your doctor's treatment. We do not diagnose or treat
              medical conditions, and nothing on this site is medical advice.
            </p>
          </Section>

          <Section title="Who else sees it">
            <p>We do not sell your information. It is shared only with the services that make this practice run:</p>
            <ul className="ml-5 list-disc space-y-2">
              <li><strong>WhatsApp and Meta</strong> — because our business WhatsApp runs on the WhatsApp Business Platform, messages pass through Meta's systems under their own terms.</li>
              <li><strong>Our hosting and database providers</strong> — who store the site and enquiry records securely on our behalf.</li>
              <li><strong>Anyone the law requires</strong> — if we are legally obliged to disclose something.</li>
            </ul>
          </Section>

          <Section title="How long we keep it">
            <p>
              Enquiry details are kept while we are in touch and for a reasonable period afterwards in case you come back to us. Client
              records are kept for as long as you are with us and for a period after, so that we can pick up where we left off if you
              return. Ask us to delete your records and we will, unless we are required to keep them.
            </p>
          </Section>

          <Section title="Your choices">
            <ul className="ml-5 list-disc space-y-2">
              <li>Ask what we hold about you.</li>
              <li>Ask us to correct anything that is wrong.</li>
              <li>Ask us to delete your information.</li>
              <li>Tell us to stop messaging you.</li>
            </ul>
            <p>
              Email <a className="link-line text-forest" href={`mailto:${SITE.email}`}>{SITE.email}</a> and we will deal with it.
            </p>
          </Section>

          <Section title="Cookies">
            <p>
              This site uses only what it needs to function and to understand general usage. It does not run advertising trackers that
              follow you around other websites.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              If this policy changes, the new version appears on this page with a new date at the top. Significant changes will be
              mentioned to active clients directly.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              {SITE.name}<br />
              {SITE.street}, {SITE.locality}<br />
              {SITE.cityName}, {SITE.region} {SITE.postalCode}, India<br />
              <a className="link-line text-forest" href={`mailto:${SITE.email}`}>{SITE.email}</a> ·{" "}
              <a className="link-line text-forest" href={`tel:${SITE.phoneTel}`}>{SITE.phoneDisplay}</a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
